import React, {useEffect, useRef, useState} from 'react';
import {Animated, TouchableOpacity, Platform, useColorScheme} from 'react-native';
import {BottomTabBarProps} from '@react-navigation/bottom-tabs';
import {useTheme} from 'styled-components/native';
import {RFValue} from 'react-native-responsive-fontsize';



import {
  TAB_HEIGHT,
  CENTER_OVERLAP,
  Wrapper,
  FloatingWrapper,
  ShadowLayer,
  BlurClip,
  StyledBlurView,
  Overlay,
  Tab,
  IconContainer,
  Label,
  CenterButtonOuter,
  CenterBlurView,
  CenterSpace,
  CENTER_BUTTON_SIZE,
} from './styles';

export function FloatingTabBar({
  state,
  descriptors,
  navigation,
  insets,
}: BottomTabBarProps) {
  const theme = useTheme();
  const colorScheme = useColorScheme();

  const isDark = colorScheme === 'dark';

  const centerIndex = Math.floor(state.routes.length / 2);

  // No Android, a barra de gestos gera um inset baixo (~24dp) enquanto a
  // barra de navegação de 3 botões gera um inset bem maior (~48dp). Usamos
  // esse limiar para saber quando reservar espaço extra sobre os botões físicos.
  const hasThreeButtonNavigation =
    Platform.OS === 'android' && insets.bottom >= 32;
  const THREE_BUTTON_NAV_EXTRA_SPACING = 10;

  const tabBarBottomCondition = insets.bottom > 0 ? insets.bottom : 10;

  const tabBarBottom =
    tabBarBottomCondition +
    (hasThreeButtonNavigation ? THREE_BUTTON_NAV_EXTRA_SPACING : 0);
  const centerButtonBottom = tabBarBottom + TAB_HEIGHT - CENTER_OVERLAP;

  const [containerWidth, setContainerWidth] = useState(0);
  const indicatorX = useRef(new Animated.Value(0)).current;
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (containerWidth === 0) {
      return;
    }
    const SIDE_PADDING = 8;
    const tabWidth = (containerWidth - SIDE_PADDING * 2) / state.routes.length;
    const targetX = SIDE_PADDING + state.index * tabWidth;
    if (isInitialRender.current) {
      indicatorX.setValue(targetX);
      isInitialRender.current = false;
      return;
    }
    Animated.spring(indicatorX, {
      toValue: targetX,
      useNativeDriver: true,
      tension: 80,
      friction: 10,
    }).start();
  }, [state.index, containerWidth]);

  const handlePress = (route: (typeof state.routes)[0], isFocused: boolean) => {
    const event = navigation.emit({
      type: 'tabPress',
      target: route.key,
      canPreventDefault: true,
    });
    if (!isFocused && !event.defaultPrevented) {
      navigation.navigate(route.name, {});
    }
  };

  const centerRoute = state.routes[centerIndex];
  const centerOptions = descriptors[centerRoute.key].options;
  const isCenterFocused = state.index === centerIndex;
  const centerActiveTint =
    (centerOptions.tabBarActiveTintColor as string) ?? theme.colors.primary;
  const centerInactiveTint =
    (centerOptions.tabBarInactiveTintColor as string) ?? theme.colors.tabInactive;

  const SIDE_PADDING = 8;
  const tabWidth =
    containerWidth > 0
      ? (containerWidth - SIDE_PADDING * 2) / state.routes.length
      : 0;

  return (
    <Wrapper pointerEvents="box-none">
      <FloatingWrapper $bottom={tabBarBottom}>
        {Platform.OS === 'android' && <ShadowLayer />}
        <BlurClip>
          <StyledBlurView
            blurType={isDark ? 'materialDark' : 'xlight'}
            blurAmount={Platform.OS === 'ios' ? 10 : 10}
            reducedTransparencyFallbackColor={isDark ? '#1A1A1A' : '#ffffff'}>
            <Overlay
              $isDark={isDark}
              onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}>
              {tabWidth > 0 && state.index !== centerIndex && (
                <Animated.View
                  style={{
                    position: 'absolute',
                    width: tabWidth,
                    height: TAB_HEIGHT - 12,
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.10)'
                      : 'rgba(0,0,0,0.07)',
                    borderRadius: RFValue(20),
                    transform: [{translateX: indicatorX}],
                    top: 6,
                  }}
                />
              )}

              {state.routes.map((route, index) => {
                const {options} = descriptors[route.key];
                const isFocused = state.index === index;
                const isCenter = index === centerIndex;

                const label =
                  typeof options.tabBarLabel === 'string'
                    ? options.tabBarLabel
                    : (options.title ?? route.name);

                const activeTint =
                  (options.tabBarActiveTintColor as string) ?? theme.colors.primary;
                const inactiveTint =
                  (options.tabBarInactiveTintColor as string) ??
                  theme.colors.tabInactive;
                const tintColor = isFocused ? activeTint : inactiveTint;
                const labelStyle =
                  (options.tabBarLabelStyle as object | undefined) ?? {};

                if (isCenter) {
                  return (
                    <Tab
                      key={route.key}
                      onPress={() => handlePress(route, isFocused)}
                      activeOpacity={0.7}>
                      <CenterSpace />
                      <Label
                        $color={tintColor}
                        $fontFamily={
                          isFocused ? theme.fonts.semibold : theme.fonts.regular
                        }
                        style={labelStyle}
                        numberOfLines={1}>
                        {label}
                      </Label>
                    </Tab>
                  );
                }

                return (
                  <Tab
                    key={route.key}
                    onPress={() => handlePress(route, isFocused)}
                    activeOpacity={0.7}>
                    <IconContainer>
                      {options.tabBarIcon?.({
                        focused: isFocused,
                        color: tintColor,
                        size: RFValue(18),
                      })}
                    </IconContainer>
                    <Label
                      $color={tintColor}
                      $fontFamily={
                        isFocused ? theme.fonts.semibold : theme.fonts.regular
                      }
                      style={labelStyle}
                      numberOfLines={1}>
                      {label}
                    </Label>
                  </Tab>
                );
              })}
            </Overlay>
          </StyledBlurView>
        </BlurClip>
      </FloatingWrapper>

      <CenterButtonOuter $bottom={centerButtonBottom} pointerEvents="box-none">
        <TouchableOpacity
          onPress={() => handlePress(centerRoute, isCenterFocused)}
          activeOpacity={0.85}>
          <CenterBlurView
          >
            {centerOptions.tabBarIcon?.({
              focused: isCenterFocused,
              color: isCenterFocused ? centerActiveTint : centerInactiveTint,
              size: CENTER_BUTTON_SIZE,
            })}
          </CenterBlurView>
        </TouchableOpacity>
      </CenterButtonOuter>
    </Wrapper>
  );
}
